import React, { useRef } from "react";
import { Carousel } from "../../atoms";
import get from "lodash/get";
import { getStoryElementByType } from "../../utils";
import YouTube from "react-youtube";
import getYouTubeID from "get-youtube-id";
import "./VideoInStory.m.css";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";

const VideoInStory = ({ moreVideos, story, config }) => {
  const pageType = "story-page";
  const playersRef = useRef({});
  const videoMetaRef = useRef({});
  const startedVideosRef = useRef(new Set());
  const playingVideosRef = useRef(new Set());
  const userHasInteractedRef = useRef(false);
  const userPausedVideosRef = useRef(new Set());
  const programmaticPausedVideosRef = useRef(new Set());

  const pauseOtherVideos = (currentVideoId) => {
    Object.entries(playersRef.current).forEach(([id, player]) => {
      if (id !== currentVideoId && player?.pauseVideo) {
        if (!playingVideosRef.current.has(id)) {
          return;
        }

        const pausedVideoMeta = videoMetaRef.current[id];

        if (pausedVideoMeta) {
          pushVideoEvent({
            action: "pause",
            title: pausedVideoMeta.title,
            videoUrl: pausedVideoMeta.videoUrl,
          });
        }

        playingVideosRef.current.delete(id);
        userPausedVideosRef.current.add(id);
        programmaticPausedVideosRef.current.add(id);
        player.pauseVideo();
      }
    });
  };

  const onPlayerReady = (event, shouldAutoPlay, videoId, videoMeta) => {
    playersRef.current[videoId] = event.target;
    videoMetaRef.current[videoId] = videoMeta;

    if (shouldAutoPlay) {
      event.target.mute(); // required for autoplay
      event.target.playVideo();
    }
  };

  const pushVideoEvent = ({ action, title, videoUrl }) => {
    if (!window?.dataLayer) return;

    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);
    window.dataLayer.push({
      event: "video_view",
      event_action: action,
      link_text: title,
      link_url: videoUrl,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
  };

  return (
    <div
      styleName="video-in-story"
      onMouseDown={() => (userHasInteractedRef.current = true)}
      onTouchStart={() => (userHasInteractedRef.current = true)}
    >
      <div styleName="video-in-story-header">
        <a href={`/${moreVideos?.slug}`}>
          <h2>Top Videos</h2>
        </a>
        <a href={`/${moreVideos?.slug}`} styleName="watch-more-button">
          Watch More
        </a>
      </div>

      <div styleName="video-in-story-body">
        <Carousel itemWidth={290} gap={12} showButtons={true} maxItemWidth={290} itemMobileWidth={300}>
          {moreVideos?.items?.map((video, index) => {
            let eventEmbed = "";

            video?.story?.cards?.forEach((card) => {
              const embedUrl = get(getStoryElementByType(card, "youtube-video"), "embed-url", "");
              if (embedUrl) eventEmbed = embedUrl;
            });

            if (!eventEmbed) return null;

            const ytId = getYouTubeID(eventEmbed);
            const playerKey = `${ytId}-${index}`;
            const isFirstVideo = index === 0;

            return (
              <div key={index} styleName="video-in-story-item">
                <YouTube
                  videoId={ytId}
                  opts={{
                    width: "100%",
                    height: "100%",
                    playerVars: {
                      autoplay: isFirstVideo ? 1 : 0,
                      mute: 1, // autoplay always muted
                      controls: 1,
                      rel: 0,
                      modestbranding: 1,
                      playsinline: 1,
                    },
                  }}
                  onReady={(e) =>
                    onPlayerReady(e, isFirstVideo, playerKey, {
                      title: video?.story?.headline,
                      videoUrl: `/${video?.story?.slug}`,
                    })
                  }
                  onStateChange={(e) => {
                    const state = e.data;
                    const title = video?.story?.headline;
                    const videoUrl = `/${video?.story?.slug}`;
                    const player = playersRef.current[playerKey];

                    if (state === 1) {
                      if (playingVideosRef.current.has(playerKey)) {
                        return;
                      }

                      if (!isFirstVideo) {
                        userHasInteractedRef.current = true;
                      }

                      pauseOtherVideos(playerKey);
                      playingVideosRef.current.add(playerKey);

                      // Keep the first autoplay muted; unmute it only after a real user pause/play.
                      if (!isFirstVideo || userPausedVideosRef.current.has(playerKey)) {
                        player?.unMute();
                      } else {
                        player?.mute();
                      }

                      if (userPausedVideosRef.current.has(playerKey)) {
                        userPausedVideosRef.current.delete(playerKey);
                        pushVideoEvent({
                          action: "resume",
                          title,
                          videoUrl,
                        });
                        return;
                      }

                      if (!startedVideosRef.current.has(playerKey) && isFirstVideo && !userHasInteractedRef.current) {
                        startedVideosRef.current.add(playerKey);
                        pushVideoEvent({
                          action: "autoplay",
                          title,
                          videoUrl,
                        });
                        return;
                      }

                      if (!startedVideosRef.current.has(playerKey)) {
                        startedVideosRef.current.add(playerKey);
                        pushVideoEvent({
                          action: "play",
                          title,
                          videoUrl,
                        });
                        return;
                      }

                      pushVideoEvent({
                        action: "play",
                        title,
                        videoUrl,
                      });
                    }

                    if (state === 2) {
                      if (programmaticPausedVideosRef.current.has(playerKey)) {
                        programmaticPausedVideosRef.current.delete(playerKey);
                        return;
                      }

                      playingVideosRef.current.delete(playerKey);

                      if (typeof document !== "undefined" && document.hidden) {
                        return;
                      }

                      userHasInteractedRef.current = true;
                      userPausedVideosRef.current.add(playerKey);
                      pushVideoEvent({
                        action: "pause",
                        title,
                        videoUrl,
                      });
                    }

                    if (state === 0) {
                      playingVideosRef.current.delete(playerKey);
                      userPausedVideosRef.current.delete(playerKey);
                      pushVideoEvent({
                        action: "end",
                        title,
                        videoUrl,
                      });
                    }
                  }}
                />

                <h3>
                  <a href={`/${video?.story?.slug}`}>{video?.story?.headline}</a>
                </h3>
              </div>
            );
          })}
        </Carousel>
      </div>
    </div>
  );
};

export default VideoInStory;
